import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Wallet, ShieldCheck, Zap, Lock, ArrowRight, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { BackgroundMusic } from '../components/BackgroundMusic';
import { useAuth } from '../contexts/AuthContext';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    heroTitle: 'Vanguard',
    heroHighlight: 'Coin',
    heroSubtitle: 'Donaciones y Retiros',
    heroDesc: 'MU Vanguard revoluciona el ecosistema Play-to-Earn con V Coin, nuestra moneda oficial. Compra V Coins para potenciar tu experiencia o convierte tus V Coins ganados en el juego directamente a USDT.',
    donateTitle: 'Comprar V Coin',
    donateDesc: 'Adquiere Vanguard Coins (V Coin) para acceder a beneficios exclusivos, objetos premium y estatus VIP. Tu donación impulsa el desarrollo del servidor y mejora la experiencia para toda la comunidad.',
    donateBtn: 'Comprar V Coin',
    donateFeatures: ['Entrega instantánea de V Coins a tu cuenta', 'Desbloqueo inmediato de estatus VIP', 'Acceso a la tienda exclusiva de V Coin', 'Múltiples métodos de pago disponibles'],
    withdrawTitle: 'V Coin → USDT',
    withdrawDesc: 'Tu esfuerzo tiene valor real. Convierte tus V Coins ganados en el juego directamente a USDT (Tether). Nuestro sistema de retiro es seguro, transparente y diseñado para recompensar a los verdaderos guerreros.',
    withdrawBtn: 'Retirar V Coin a USDT',
    withdrawFeatures: ['Conversión directa de V Coin a USDT', 'Tasa de cambio transparente y actualizada', 'Retiro a tu wallet de Binance o TRC20', 'Procesamiento verificado en 24-48 horas'],
    featuresTitle: 'Transacciones',
    featuresHighlight: 'Seguras',
    features: [{
      icon: ShieldCheck,
      title: 'Pagos Protegidos',
      desc: 'Todas las transacciones de V Coin están encriptadas de extremo a extremo para garantizar la máxima seguridad de tus fondos.'
    }, {
      icon: Zap,
      title: 'Procesamiento Rápido',
      desc: 'Compra de V Coins instantánea. Retiros procesados por nuestro equipo dedicado en tiempo récord.'
    }, {
      icon: Lock,
      title: 'Economía Estable',
      desc: 'Monitoreo constante del valor del V Coin contra la inflación y bots para proteger tu inversión.'
    }],
    faqTitle: 'Preguntas',
    faqHighlight: 'Frecuentes',
    faqs: [{
      q: '¿Qué es V Coin (Vanguard Coin)?',
      a: 'V Coin es la moneda oficial de MU Vanguard. Puedes comprarla con dinero real para obtener beneficios premium, o ganarla jugando y luego convertirla a USDT.'
    }, {
      q: '¿Qué métodos de pago aceptan para comprar V Coins?',
      a: 'Aceptamos PayPal, Binance Pay, USDT (TRC20), tarjetas de crédito/débito principales y transferencias bancarias locales en países seleccionados.'
    }, {
      q: '¿Cuál es el monto mínimo para retirar V Coins a USDT?',
      a: 'El monto mínimo para solicitar un retiro es el equivalente a $20 USD en V Coins. Esto nos permite mantener las comisiones de red bajas para todos los jugadores.'
    }, {
      q: '¿Cuánto tiempo tarda un retiro de V Coin a USDT?',
      a: 'Los retiros pasan por un proceso de verificación de seguridad y normalmente se envían a tu wallet en un plazo de 24 a 48 horas hábiles.'
    }, {
      q: '¿Hay comisiones por retirar V Coins?',
      a: 'Aplicamos una tarifa del 3% para cubrir los costos de transacción de la red blockchain y mantenimiento del sistema. El resto es 100% tuyo.'
    }]
  },
  en: {
    heroTitle: 'Vanguard',
    heroHighlight: 'Coin',
    heroSubtitle: 'Donations & Withdrawals',
    heroDesc: 'MU Vanguard revolutionizes the Play-to-Earn ecosystem with V Coin, our official currency. Purchase V Coins to power up your experience or convert your in-game earned V Coins directly to USDT.',
    donateTitle: 'Purchase V Coin',
    donateDesc: 'Acquire Vanguard Coins (V Coin) to access exclusive benefits, premium items, and VIP status. Your donation fuels server development and enhances the experience for the entire community.',
    donateBtn: 'Buy V Coin',
    donateFeatures: ['Instant V Coin delivery to your account', 'Immediate VIP status unlock', 'Access to the exclusive V Coin shop', 'Multiple payment methods available'],
    withdrawTitle: 'V Coin → USDT',
    withdrawDesc: 'Your effort has real value. Convert your in-game earned V Coins directly to USDT (Tether). Our withdrawal system is secure, transparent, and designed to reward true warriors.',
    withdrawBtn: 'Withdraw V Coin to USDT',
    withdrawFeatures: ['Direct V Coin to USDT conversion', 'Transparent and updated exchange rate', 'Withdraw to your Binance or TRC20 wallet', 'Verified processing within 24-48 hours'],
    featuresTitle: 'Secure',
    featuresHighlight: 'Transactions',
    features: [{
      icon: ShieldCheck,
      title: 'Protected Payments',
      desc: 'All V Coin transactions are end-to-end encrypted to ensure the maximum security of your funds.'
    }, {
      icon: Zap,
      title: 'Fast Processing',
      desc: 'Instant V Coin purchases. Withdrawals processed by our dedicated team in record time.'
    }, {
      icon: Lock,
      title: 'Stable Economy',
      desc: 'Constant monitoring of V Coin value against inflation and bots to protect your investment.'
    }],
    faqTitle: 'Frequently Asked',
    faqHighlight: 'Questions',
    faqs: [{
      q: 'What is V Coin (Vanguard Coin)?',
      a: 'V Coin is the official currency of MU Vanguard. You can buy it with real money for premium benefits, or earn it by playing and then convert it to USDT.'
    }, {
      q: 'What payment methods do you accept for V Coin purchases?',
      a: 'We accept PayPal, Binance Pay, USDT (TRC20), major credit/debit cards, and local bank transfers in selected countries.'
    }, {
      q: 'What is the minimum amount to withdraw V Coins to USDT?',
      a: 'The minimum amount to request a withdrawal is the equivalent of $20 USD in V Coins. This helps us keep network fees low for all players.'
    }, {
      q: 'How long does a V Coin to USDT withdrawal take?',
      a: 'Withdrawals go through a security verification process and are typically sent to your wallet within 24 to 48 business hours.'
    }, {
      q: 'Are there fees for withdrawing V Coins?',
      a: 'We apply a 3% fee to cover blockchain network transaction costs and system maintenance. The rest is 100% yours.'
    }]
  },
  pt: {
    heroTitle: 'Vanguard',
    heroHighlight: 'Coin',
    heroSubtitle: 'Doações e Saques',
    heroDesc: 'MU Vanguard revoluciona o ecossistema Play-to-Earn com V Coin, nossa moeda oficial. Compre V Coins para potencializar sua experiência ou converta seus V Coins ganhos no jogo diretamente para USDT.',
    donateTitle: 'Comprar V Coin',
    donateDesc: 'Adquira Vanguard Coins (V Coin) para acessar benefícios exclusivos, itens premium e status VIP. Sua doação impulsiona o desenvolvimento do servidor e melhora a experiência para toda a comunidade.',
    donateBtn: 'Comprar V Coin',
    donateFeatures: ['Entrega instantânea de V Coins na sua conta', 'Desbloqueio imediato de status VIP', 'Acesso à loja exclusiva de V Coin', 'Múltiplos métodos de pagamento disponíveis'],
    withdrawTitle: 'V Coin → USDT',
    withdrawDesc: 'Seu esforço tem valor real. Converta seus V Coins ganhos no jogo diretamente para USDT (Tether). Nosso sistema de saque é seguro, transparente e projetado para recompensar os verdadeiros guerreiros.',
    withdrawBtn: 'Sacar V Coin para USDT',
    withdrawFeatures: ['Conversão direta de V Coin para USDT', 'Taxa de câmbio transparente e atualizada', 'Saque para sua wallet Binance ou TRC20', 'Processamento verificado em 24-48 horas'],
    featuresTitle: 'Transações',
    featuresHighlight: 'Seguras',
    features: [{
      icon: ShieldCheck,
      title: 'Pagamentos Protegidos',
      desc: 'Todas as transações de V Coin são criptografadas de ponta a ponta para garantir a máxima segurança de seus fundos.'
    }, {
      icon: Zap,
      title: 'Processamento Rápido',
      desc: 'Compra de V Coins instantânea. Saques processados por nossa equipe dedicada em tempo recorde.'
    }, {
      icon: Lock,
      title: 'Economia Estável',
      desc: 'Monitoramento constante do valor do V Coin contra inflação e bots para proteger seu investimento.'
    }],
    faqTitle: 'Perguntas',
    faqHighlight: 'Frequentes',
    faqs: [{
      q: 'O que é V Coin (Vanguard Coin)?',
      a: 'V Coin é a moeda oficial do MU Vanguard. Você pode comprá-la com dinheiro real para benefícios premium, ou ganhá-la jogando e depois convertê-la para USDT.'
    }, {
      q: 'Quais métodos de pagamento vocês aceitam para comprar V Coins?',
      a: 'Aceitamos PayPal, Binance Pay, USDT (TRC20), principais cartões de crédito/débito e transferências bancárias locais em países selecionados.'
    }, {
      q: 'Qual é o valor mínimo para sacar V Coins para USDT?',
      a: 'O valor mínimo para solicitar um saque é o equivalente a $20 USD em V Coins. Isso nos ajuda a manter as taxas de rede baixas para todos os jogadores.'
    }, {
      q: 'Quanto tempo leva um saque de V Coin para USDT?',
      a: 'Os saques passam por um processo de verificação de segurança e normalmente são enviados para sua wallet dentro de 24 a 48 horas úteis.'
    }, {
      q: 'Existem taxas para sacar V Coins?',
      a: 'Aplicamos uma taxa de 3% para cobrir os custos de transação da rede blockchain e manutenção do sistema. O resto é 100% seu.'
    }]
  }
};
const loginMessages = {
  es: 'Debes iniciar sesión para acceder a esta función',
  en: 'You must log in to access this feature',
  pt: 'Você deve fazer login para acessar este recurso'
};
export function DonationPage() {
  const [language, setLanguage] = useState<Language>('es');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const navigate = useNavigate();
  const {
    isAuthenticated
  } = useAuth();
  const handleProtectedAction = (action: 'donate' | 'withdraw') => {
    const intent = action === 'donate' ? 'buy' : 'withdraw';
    navigate(`/dashboard?intent=${intent}`);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && ['es', 'en', 'pt'].includes(savedLang)) {
      setLanguage(savedLang);
    }
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  const t = translations[language];
  return <div className="min-h-screen bg-darkBlue text-white font-sans selection:bg-gold/30 selection:text-white" data-id="element-666">
      <BackgroundMusic data-id="element-667" />
      <Navigation data-id="element-668" />

      <main className="relative overflow-hidden" data-id="element-669">
        {/* Hero Section with Dragon Background */}
        <div className="relative pt-32 pb-24" data-id="element-670">
          {/* Full Background Image */}
          <div className="absolute inset-0" data-id="element-671">
            <img src="https://cdn.magicpatterns.com/uploads/kC9bZ18PLLpFhURpafcsss/PHOTO-2025-12-20-20-23-39.jpg" alt="V Coin Dragon Treasury" className="w-full h-full object-cover" data-id="element-672" />
            <div className="absolute inset-0 bg-darkBlue/40" data-id="element-673" />
            <div className="absolute inset-0 bg-gradient-to-b from-darkBlue/50 via-transparent to-darkBlue/90" data-id="element-674" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-675">
            {/* Hero Content */}
            <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-676">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-677">
                <span className="inline-block py-1 px-3 rounded-full bg-gold/10 border border-gold/30 text-gold text-sm font-bold tracking-wider uppercase mb-6" data-id="element-678">
                  {t.heroSubtitle}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-cinzel" data-id="element-679">
                  {t.heroTitle}{' '}
                  <span className="text-gradient-gold" data-id="element-680">{t.heroHighlight}</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-681">
                  {t.heroDesc}
                </p>
              </motion.div>
            </div>

            {/* Action Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 pb-24" data-id="element-682">
              {/* Donate Card */}
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="glass-panel rounded-2xl p-8 md:p-10 border border-gold/20 relative overflow-hidden group hover:border-gold/50 transition-colors duration-500" data-id="element-683">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-[80px] -mr-32 -mt-32 transition-transform duration-700 group-hover:scale-150" data-id="element-684" />

                <div className="relative z-10" data-id="element-685">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold/20 to-yellow-600/20 flex items-center justify-center border border-gold/30 mb-8" data-id="element-686">
                    <CreditCard className="w-8 h-8 text-gold" data-id="element-687" />
                  </div>

                  <h2 className="text-3xl font-bold mb-4 font-cinzel text-white group-hover:text-gold transition-colors" data-id="element-688">
                    {t.donateTitle}
                  </h2>
                  <p className="text-gray-300 mb-8 leading-relaxed min-h-[80px]" data-id="element-689">
                    {t.donateDesc}
                  </p>

                  <ul className="space-y-4 mb-10" data-id="element-690">
                    {t.donateFeatures.map((feature, idx) => <li key={idx} className="flex items-center gap-3 text-gray-200" data-id="element-691">
                        <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0" data-id="element-692">
                          <div className="w-2 h-2 rounded-full bg-gold" data-id="element-693" />
                        </div>
                        {feature}
                      </li>)}
                  </ul>

                  <button onClick={() => handleProtectedAction('donate')} data-testid="donation-buy-vcoin-action" data-field="account.vCoinBalance" className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2 group/btn" data-id="element-694">
                    {t.donateBtn}
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" data-id="element-695" />
                  </button>
                  {!isAuthenticated && <p data-testid="donation-login-required" className="mt-4 text-center text-sm text-gold" data-id="element-696">
                      {loginMessages[language]}
                    </p>}
                </div>
              </motion.div>

              {/* Withdraw Card */}
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="glass-panel rounded-2xl p-8 md:p-10 border border-glowBlue/20 relative overflow-hidden group hover:border-glowBlue/50 transition-colors duration-500" data-id="element-697">
                <div className="absolute top-0 right-0 w-64 h-64 bg-glowBlue/10 rounded-full blur-[80px] -mr-32 -mt-32 transition-transform duration-700 group-hover:scale-150" data-id="element-698" />

                <div className="relative z-10" data-id="element-699">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-glowBlue/20 to-blue-600/20 flex items-center justify-center border border-glowBlue/30 mb-8" data-id="element-700">
                    <Wallet className="w-8 h-8 text-glowBlue" data-id="element-701" />
                  </div>

                  <h2 className="text-3xl font-bold mb-4 font-cinzel text-white group-hover:text-glowBlue transition-colors" data-id="element-702">
                    {t.withdrawTitle}
                  </h2>
                  <p className="text-gray-300 mb-8 leading-relaxed min-h-[80px]" data-id="element-703">
                    {t.withdrawDesc}
                  </p>

                  <ul className="space-y-4 mb-10" data-id="element-704">
                    {t.withdrawFeatures.map((feature, idx) => <li key={idx} className="flex items-center gap-3 text-gray-200" data-id="element-705">
                        <div className="w-5 h-5 rounded-full bg-glowBlue/20 flex items-center justify-center flex-shrink-0" data-id="element-706">
                          <div className="w-2 h-2 rounded-full bg-glowBlue" data-id="element-707" />
                        </div>
                        {feature}
                      </li>)}
                  </ul>

                  <button onClick={() => handleProtectedAction('withdraw')} data-testid="donation-withdraw-vcoin-action" data-field="account.withdrawal" className="w-full bg-transparent border-2 border-glowBlue text-glowBlue hover:bg-glowBlue hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 uppercase tracking-wider flex items-center justify-center gap-2 group/btn shadow-[0_0_15px_rgba(74,158,255,0.2)] hover:shadow-[0_0_25px_rgba(74,158,255,0.5)]" data-id="element-708">
                    {t.withdrawBtn}
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" data-id="element-709" />
                  </button>
                  {!isAuthenticated && <p className="mt-4 text-center text-sm text-glowBlue" data-id="element-710">
                      {loginMessages[language]}
                    </p>}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Sections below hero background */}
        <div className="bg-darkerBlue relative" data-id="element-711">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-712">
            {/* Security Features */}
            <div className="mb-24" data-id="element-713">
              <div className="text-center mb-12" data-id="element-714">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-715">
                  {t.featuresTitle}{' '}
                  <span className="text-gradient-gold" data-id="element-716">
                    {t.featuresHighlight}
                  </span>
                </h2>
                <div className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-717" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-718">
                {t.features.map((feature, idx) => {
                const Icon = feature.icon;
                return <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: idx * 0.2
                }} className="text-center p-6" data-id="element-719">
                      <div className="w-16 h-16 mx-auto rounded-full bg-darkerBlue border border-white/10 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(212,175,55,0.15)]" data-id="element-720">
                        <Icon className="w-8 h-8 text-gold" data-id="element-721" />
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-white" data-id="element-722">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed" data-id="element-723">
                        {feature.desc}
                      </p>
                    </motion.div>;
              })}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto" data-id="element-724">
              <div className="text-center mb-12" data-id="element-725">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-726">
                  {t.faqTitle}{' '}
                  <span className="text-gradient-gold" data-id="element-727">{t.faqHighlight}</span>
                </h2>
              </div>

              <div className="space-y-4" data-id="element-728">
                {t.faqs.map((faq, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.1
              }} className="glass-panel border border-white/5 rounded-xl overflow-hidden" data-id="element-729">
                    <button onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors" data-id="element-730">
                      <span className="font-bold text-lg pr-8" data-id="element-731">{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform duration-300 ${openFaqIndex === idx ? 'rotate-180' : ''}`} data-id="element-732" />
                    </button>
                    <AnimatePresence data-id="element-733">
                      {openFaqIndex === idx && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-734">
                          <div className="px-6 pb-5 text-gray-400 leading-relaxed border-t border-white/5 pt-4" data-id="element-735">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-736" />
    </div>;
}